import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api/client';
import type { Marca } from '../lib/api/types';
import { useAuth } from '../lib/auth/AuthContext';
import { PageHeader } from '../components/ui/PageHeader';
import { Carregando, Erro, Vazio } from '../components/ui/Estados';

/**
 * As marcas da empresa (A5).
 *
 * <p>A hierarquia é empresa → marca → produto → lote → item (ADR-039). Uma empresa de marca
 * única nunca precisa abrir esta tela — e é por isso que o seletor da barra some nesse caso.
 */
export function MarcasPage() {
  const { temPapel } = useAuth();
  const clienteDeConsulta = useQueryClient();
  const [nome, setNome] = useState('');
  const [slug, setSlug] = useState('');

  const marcas = useQuery({
    queryKey: ['marcas'],
    queryFn: () => api.get<Marca[]>('/console/brands'),
  });

  const criar = useMutation({
    mutationFn: () => api.post<Marca>('/console/brands', { name: nome, slug }),
    onSuccess: () => {
      setNome('');
      setSlug('');
      void clienteDeConsulta.invalidateQueries({ queryKey: ['marcas'] });
    },
  });

  return (
    <>
      <PageHeader
        titulo="Marcas"
        subtitulo="A identidade que o passaporte público usa. Produtos e lotes pendem de uma marca."
      />
      <div className="wrap">
        <div className="duas">
          <div className="card tbl">
            {marcas.isPending && <Carregando o="as marcas" />}
            {marcas.isError && <Erro erro={marcas.error} />}
            {marcas.data && marcas.data.length === 0 && (
              <Vazio titulo="Nenhuma marca" detalhe="Crie a primeira ao lado." />
            )}
            {marcas.data && marcas.data.length > 0 && (
              <table className="t">
                <thead>
                  <tr>
                    <th>Marca</th>
                    <th>Identificador</th>
                    <th>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {marcas.data.map((m) => (
                    <tr key={m.id}>
                      <td style={{ fontWeight: 600 }}>{m.name}</td>
                      <td className="mono-s">{m.slug}</td>
                      <td>
                        <span className="chip chip-neutral">{m.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {temPapel('COMPANY_ADMIN', 'COMPANY_CATALOG') && (
            <div className="card cd">
              <div className="cd2-t">Nova marca</div>
              <form
                className="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  criar.mutate();
                }}
              >
                <div className="campo">
                  <label htmlFor="n">Nome</label>
                  <input id="n" value={nome} onChange={(e) => setNome(e.target.value)} required />
                </div>
                <div className="campo">
                  <label htmlFor="s">Identificador</label>
                  <input
                    id="s"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    pattern="[a-z0-9-]+"
                    required
                  />
                  <span className="dica">
                    Minúsculas e hífen. Ele entra em endereço público e não muda depois.
                  </span>
                </div>
                {criar.isError && <div className="aviso ruim">{String(criar.error)}</div>}
                <button className="btn btn-primary" type="submit" disabled={criar.isPending}>
                  Criar marca
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
